import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Switch } from '@headlessui/react';
import {
  ShoppingBagIcon,
  BuildingStorefrontIcon,
  GlobeAltIcon,
  ChatBubbleLeftIcon
} from '@heroicons/react/24/outline';
import { MetaLogo, GoogleAdsLogo, KlaviyoLogo, ShopifyLogo } from '../icons';
import { toast } from 'react-hot-toast';
import { onboardingApi } from '../../services/api/onboarding';

function ConnectChannels({ onNext, onPrevious }) {
  const [connections, setConnections] = useState({
    marketplaces: {
      amazonUAE: false,
      noonUAE: false,
      jumia: false,
      carrefour: false
    },
    marketing: {
      meta: false,
      googleAds: false,
      klaviyo: false
    },
    sales: {
      shopify: false,
      woocommerce: false,
      magento: false
    }
  });

  // Function to handle authorization window
  const handleAuthorization = (platform, category) => {
    const authUrls = {
      shopify: 'https://your-api.com/auth/shopify',
      meta: 'https://your-api.com/auth/meta',
      googleAds: 'https://your-api.com/auth/google-ads',
      klaviyo: 'https://your-api.com/auth/klaviyo',
      amazonUAE: 'https://your-api.com/auth/amazon',
      noonUAE: 'https://your-api.com/auth/noon',
      jumia: 'https://your-api.com/auth/jumia',
      carrefour: 'https://your-api.com/auth/carrefour',
      woocommerce: 'https://your-api.com/auth/woocommerce',
      magento: 'https://your-api.com/auth/magento'
    };

    // Calculate center position
    const width = 600;
    const height = 600;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    // Open authorization window with centered position
    const authWindow = window.open(
      authUrls[platform],
      'Authorization',
      `width=${width},
       height=${height},
       left=${left},
       top=${top},
       toolbar=no,
       location=no,
       status=no,
       menubar=no,
       scrollbars=yes,
       resizable=yes`
    );

    // Listen for messages from the auth window
    const handleMessage = (event) => {
      // Verify the origin of the message
      // if (event.origin !== 'https://your-api.com') return;

      // if (event.data.success) {
        // Update connection state after successful authorization
        setConnections(prev => ({
          ...prev,
          [category]: {
            ...prev[category],
            [platform]: true
          }
        }));
      // }

      // Remove event listener and close window
      window.removeEventListener('message', handleMessage);
      authWindow.close();
    };

    window.addEventListener('message', handleMessage);
  };

  const ConnectionBox = ({ label, icon: Icon, isSelected, onClick, platform, category }) => (
    <div
      onClick={() => handleAuthorization(platform, category)}
      className={`
        cursor-pointer p-4 rounded-xl border-2 transition-all duration-200
        ${isSelected 
          ? 'border-[#0049ac] bg-[#0049ac]/5' 
          : 'border-gray-200 hover:border-gray-300 bg-white'}
      `}
    >
      <div className="flex items-center space-x-3">
        {Icon && <Icon className={`h-6 w-6 ${isSelected ? 'text-[#0049ac]' : 'text-gray-500'}`} />}
        <span className={`font-medium ${isSelected ? 'text-[#0049ac]' : 'text-gray-700'}`}>
          {label}
        </span>
      </div>
    </div>
  );

  const hasSelectionInCategory = (category) => {
    return Object.values(connections[category]).some(value => value === true);
  };

  const canContinue = () => {
    const hasMarketplaces = true;
    const hasMarketing = true;
    const hasSales = hasSelectionInCategory('sales');

    return (hasMarketplaces && hasMarketing) || 
           (hasMarketing && hasSales) || 
           (hasMarketplaces && hasMarketing && hasSales);
  };

  const handleToggle = (category, platform) => {
    setConnections(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [platform]: !prev[category][platform]
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await onboardingApi.saveChannels({
        marketplaces: {
          amazonUAE: connections.marketplaces.amazonUAE,
          noonUAE: connections.marketplaces.noonUAE,
          jumia: connections.marketplaces.jumia,
          carrefour: connections.marketplaces.carrefour
        },
        marketing: {
          meta: connections.marketing.meta,
          googleAds: connections.marketing.googleAds,
          klaviyo: connections.marketing.klaviyo
        },
        sales: {
          shopify: connections.sales.shopify,
          woocommerce: connections.sales.woocommerce,
          magento: connections.sales.magento
        }
      });
      toast.success('Channels saved successfully');
      onNext();
    } catch (error) {
      console.error('Failed to save channels:', error);
      toast.error(error.response?.data?.error || 'Failed to save channels');
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <h1 className="text-3xl text-center font-bold text-gray-900 mb-4">Connect Your Channels</h1>
      <p className="text-center text-gray-600 mb-8">Select the platforms you want to integrate with</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
        {/* Marketplaces */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center space-x-2 mb-6">
            <BuildingStorefrontIcon className="h-6 w-6 text-[#0049ac]" />
            <h2 className="text-xl font-semibold">Marketplaces</h2>
          </div>

          <div className="space-y-3">
            {Object.entries({
              amazonUAE: { label: 'Amazon UAE', icon: BuildingStorefrontIcon },
              noonUAE: { label: 'Noon UAE', icon: BuildingStorefrontIcon },
              jumia: { label: 'Jumia', icon: BuildingStorefrontIcon },
              carrefour: { label: 'Carrefour', icon: BuildingStorefrontIcon }
            }).map(([key, { label, icon }]) => (
              <ConnectionBox
                key={key}
                label={label}
                icon={icon}
                isSelected={connections.marketplaces[key]}
                platform={key}
                category="marketplaces"
              />
            ))}
          </div>
        </div>

        {/* Marketing Platforms */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center space-x-2 mb-6">
            <ChatBubbleLeftIcon className="h-6 w-6 text-[#0049ac]" />
            <h2 className="text-xl font-semibold">Marketing</h2>
          </div>

          <div className="space-y-3">
            <ConnectionBox
              label="Meta Ads"
              icon={MetaLogo}
              isSelected={connections.marketing.meta}
              platform="meta"
              category="marketing"
            />
            <ConnectionBox
              label="Google Ads"
              icon={GoogleAdsLogo}
              isSelected={connections.marketing.googleAds}
              platform="googleAds"
              category="marketing"
            />
            <ConnectionBox
              label="Klaviyo"
              icon={KlaviyoLogo}
              isSelected={connections.marketing.klaviyo}
              platform="klaviyo"
              category="marketing"
            />
          </div>
        </div>

        {/* Sales Channels */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center space-x-2 mb-6">
            <ShoppingBagIcon className="h-6 w-6 text-[#0049ac]" />
            <h2 className="text-xl font-semibold">Sales Channels</h2>
          </div>

          <div className="space-y-3">
            <ConnectionBox
              label="Shopify"
              icon={ShopifyLogo}
              isSelected={connections.sales.shopify}
              platform="shopify"
              category="sales"
            />
            {Object.entries({
              woocommerce: { label: 'WooCommerce', icon: ShoppingBagIcon },
              magento: { label: 'Magento', icon: ShoppingBagIcon }
            }).map(([key, { label, icon }]) => (
              <ConnectionBox
                key={key}
                label={label}
                icon={icon}
                isSelected={connections.sales[key]}
                platform={key}
                category="sales"
              />
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-between mt-8">
        <button
          onClick={onPrevious}
          className="px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
        >
          Previous
        </button>
        {canContinue() && (
          <button
            onClick={handleSubmit}
            className="px-6 py-3 bg-[#0049ac] text-white rounded-lg hover:bg-[#0049ac]/90 transition-colors duration-200 font-medium"
          >
            Continue
          </button>
        )}
      </div>

      <div className="fixed bottom-8 right-8 z-50">
        {/* {canContinue() && (
          <button
            onClick={onNext}
            className="px-6 py-3 bg-[#0049ac] text-white rounded-lg hover:bg-[#0049ac]/90 transition-colors duration-200 font-medium shadow-lg hover:shadow-xl"
          >
            Continue
          </button>
        )} */}
      </div>
    </div>
  );
}

export default ConnectChannels;